import { useCallback } from "react";

import { icuCountryName } from "@/shared/data/phone-country-list";
import useTranslations from "@/shared/lang/useTranslations";
import { countries } from "@/shared/utils/countries";

const ISO2_BY_ISO3 = new Map(
  countries.filter((c) => !!c.iso2).map((c) => [c.code.toUpperCase(), c.iso2 as string]),
);

/** Resolves an ISO3 code to its translated name, falling back to the catalog's own (English) label. */
export type CountryLabelResolver = (iso3: string, fallback: string) => string;

/**
 * Country names for the current locale. The backend stores and validates ISO3 codes, so the code is the value
 * and this only decides what the user reads — never what is sent.
 *
 * Names come from `Intl.DisplayNames`, which covers the whole ISO 3166-1 catalog in every locale the app
 * speaks. This used to be a hand-written table of the six countries the identity selectors offered, with the
 * explicit rule that a seventh had to be added there or it would render in English — a rule that turned
 * widening those selectors to the onboarding allowlist into eleven new keys in three files. The ICU data was
 * already in the runtime and agrees with the curated names word for word, so the table bought nothing.
 *
 * The `fallback` is the last resort, for a code ICU does not name: never the bare code, which reads as a bug.
 */
export function useCountryLabel(): CountryLabelResolver {
  const { activeLanguage } = useTranslations("global");

  return useCallback(
    (iso3: string, fallback: string) => {
      const code = iso3.trim().toUpperCase();
      const iso2 = code.length === 2 ? code : ISO2_BY_ISO3.get(code);
      if (!iso2) return fallback;
      // `icuCountryName`, not `localizedCountryName`: the caller's own label has to win when ICU has no name
      // for the country, and that only works if "no name" is distinguishable. `localizedCountryName` would
      // have already substituted the PHONE catalog's English name — a smaller list than the one these
      // callers read from, so its answer is not better than theirs.
      return icuCountryName(iso2, activeLanguage) ?? fallback;
    },
    [activeLanguage],
  );
}
